import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { SelectOption } from "./CustomSelect";
import { ErrorMessage } from "formik";

type CustomComboboxProps = {
  options: SelectOption[];
  placeholder: string;
  notFoundMessage?: React.ReactNode;
  value?: string;
  onChange?: (currentValue: string) => void;
  inputClassName?: string;
  className?: string;
  searchPlaceholder?: string;
  name?: string;
  label?: string;
  labelClassName?: string;
  optionsContainerClassName?: string;
  containerClassName?: string;
  id?: string;
};

const CustomCombobox = ({
  id,
  options,
  placeholder = "Select option",
  className,
  notFoundMessage = "No results found.",
  value,
  name,
  inputClassName,
  onChange,
  searchPlaceholder,
  label,
  optionsContainerClassName,
  containerClassName,
  labelClassName,
}: CustomComboboxProps) => {
  const [open, setOpen] = React.useState(false);
  const [_value, _setValue] = React.useState(value || "");

  return (
    <div className={containerClassName}>
      {!!label && (
        <label
          htmlFor={id}
          className={cn(
            "mb-1 block text-sm font-medium text-[#495364]",
            labelClassName,
          )}
        >
          {label}
        </label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "w-full justify-between border-input",
              value ? "text-foreground" : "text-muted-foreground/60",
              className,
            )}
          >
            {value
              ? options.find((option) => option.value === value)?.label
              : placeholder}
            <ChevronsUpDown className="ml-2 opacity-50 size-4 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          className={cn("p-0", optionsContainerClassName)}
        >
          <Command
            filter={(_option, search, keywords) => {
              if (!search) return 0;
              return Number(
                keywords?.some((keyword) =>
                  keyword?.toLowerCase().includes(search.toLowerCase()),
                ),
              );
            }}
            className="w-full"
          >
            <CommandInput
              className={inputClassName}
              placeholder={searchPlaceholder || "Search..."}
            />
            <CommandList>
              <CommandEmpty>{notFoundMessage}</CommandEmpty>
              <CommandGroup className="overflow-y-auto">
                {options.map((option) => (
                  <CommandItem
                    keywords={[option.label]}
                    key={option.value}
                    value={option.value as string}
                    onSelect={(currentValue) => {
                      _setValue(currentValue === _value ? "" : currentValue);
                      onChange?.(currentValue === value ? "" : currentValue);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "size mr-2",
                        value === option.value ? "opacity-100" : "opacity-0",
                      )}
                    />
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {name && (
        <ErrorMessage
          name={name}
          component="div"
          className="block mt-1 text-xs text-destructive"
        />
      )}
    </div>
  );
};

export default CustomCombobox;
